import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Chip, CircularProgress, Button,
  TextField, MenuItem, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow
} from '@mui/material';
import PaymentsIcon from '@mui/icons-material/Payments';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import api from '../api';
import { conEmpresa } from '../empresa';
import { tablaAncha, filtro } from '../estilos/responsivo';
import Tile, { REJILLA_TILES, pesos } from './Tile';
import { CONTENEDOR_TABLA } from '../estilos/tarjeta';

const metodoLabel = {
  transferencia: { label: 'Transferencia', bg: '#dbeafe', color: '#2563eb' },
  tarjeta: { label: 'Tarjeta', bg: '#ede9fe', color: '#7c3aed' },
  efectivo: { label: 'Efectivo', bg: '#d1fae5', color: '#059669' },
  stripe: { label: 'Stripe', bg: '#fef3c7', color: '#d97706' },
  otro: { label: 'Otro', bg: '#f1f5f9', color: '#64748b' },
};

export default function Pagos() {
  const navigate = useNavigate();
  const [data, setData] = useState({ pagos: [], total: 0, cantidad: 0 });
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroMetodo, setFiltroMetodo] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroDesde, setFiltroDesde] = useState('');
  const [filtroHasta, setFiltroHasta] = useState('');

  const fetchData = async () => {
    try {
      const params = {};
      if (filtroMetodo) params.metodo_pago = filtroMetodo;
      if (filtroCliente) params.tercero_id = filtroCliente;
      if (filtroDesde) params.fecha_desde = filtroDesde;
      if (filtroHasta) params.fecha_hasta = filtroHasta;

      const [pRes, cRes] = await Promise.all([
        api.get('/pagos', { params }),
        api.get('/terceros?rol=cliente'),
      ]);
      setData(pRes.data);
      setClientes(cRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroMetodo, filtroCliente, filtroDesde, filtroHasta]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography sx={{ color: '#64748b', fontSize: 13 }}>
            {data.cantidad} pago{data.cantidad !== 1 ? 's' : ''} — Total: {pesos(data.total)}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<FileDownloadIcon />}
          onClick={() => window.open(conEmpresa(`${api.defaults.baseURL}/pagos/exportar-excel`), '_blank')}
          sx={{ borderColor: '#059669', color: '#059669', borderRadius: 1, textTransform: 'none', fontWeight: 600 }}>
          Excel
        </Button>
      </Box>

      {/* KPIs rapidos */}
      <Box sx={{ ...REJILLA_TILES, mb: 2 }}>
        {(() => {
          const porMetodo = {};
          data.pagos.forEach(p => {
            const m = p.metodo_pago || 'otro';
            porMetodo[m] = (porMetodo[m] || 0) + p.monto;
          });
          return ['transferencia', 'tarjeta', 'efectivo', 'stripe'].map(m => {
            const info = metodoLabel[m] || metodoLabel.otro;
            return (
              <Tile key={m} label={info.label} v={porMetodo[m] || 0}
                color={info.color} bg={`${info.color}1a`} />
            );
          });
        })()}
      </Box>

      {/* Filtros */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Desde" type="date" value={filtroDesde}
          onChange={(e) => setFiltroDesde(e.target.value)}
          size="small" InputLabelProps={{ shrink: true }} sx={filtro(140)}
        />
        <TextField
          label="Hasta" type="date" value={filtroHasta}
          onChange={(e) => setFiltroHasta(e.target.value)}
          size="small" InputLabelProps={{ shrink: true }} sx={filtro(140)}
        />
        <TextField select label="Metodo" value={filtroMetodo}
          onChange={(e) => setFiltroMetodo(e.target.value)} size="small" sx={filtro(150)}>
          <MenuItem value="">Todos</MenuItem>
          {Object.entries(metodoLabel).map(([k, v]) => (
            <MenuItem key={k} value={k}>{v.label}</MenuItem>
          ))}
        </TextField>
        <TextField select label="Cliente" value={filtroCliente}
          onChange={(e) => setFiltroCliente(e.target.value)} size="small" sx={filtro(200)}>
          <MenuItem value="">Todos</MenuItem>
          {clientes.map(c => <MenuItem key={c.id} value={c.id}>{c.razon_social}</MenuItem>)}
        </TextField>
      </Box>

      <Paper elevation={0} sx={CONTENEDOR_TABLA}>
        {data.pagos.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <PaymentsIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1.5 }} />
            <Typography sx={{ color: '#1e293b', fontSize: 16, fontWeight: 600 }}>No hay pagos registrados</Typography>
            <Typography sx={{ color: '#64748b', fontSize: 13 }}>Los pagos se registran desde cada factura</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table sx={tablaAncha(820)}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>FECHA</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>CLIENTE</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>FACTURA</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }} align="right">MONTO</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>METODO</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>REFERENCIA</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>ESTADO FACTURA</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.pagos.map((p) => {
                  const ml = metodoLabel[p.metodo_pago] || metodoLabel.otro;
                  const fecha = p.fecha_pago ? new Date(p.fecha_pago).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                  return (
                    <TableRow key={p.id} hover sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }}
                      onClick={() => navigate(`/facturas/${p.factura_id}`)}>
                      <TableCell sx={{ fontSize: 13, color: '#1e293b' }}>{fecha}</TableCell>
                      <TableCell sx={{ fontSize: 13, color: '#1e293b' }}>{p.tercero_nombre}</TableCell>
                      <TableCell sx={{ fontSize: 13, fontWeight: 600, color: '#1e3a5f' }}>{p.numero_factura}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700, color: '#059669' }}>{pesos(p.monto)}</TableCell>
                      <TableCell>
                        <Chip label={ml.label} size="small" sx={{ height: 22, fontSize: 10, fontWeight: 600, bgcolor: ml.bg, color: ml.color }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 13, color: '#64748b' }}>{p.referencia || '—'}</TableCell>
                      <TableCell>
                        <Chip label={p.factura_estado} size="small" sx={{
                          height: 22, fontSize: 10, fontWeight: 600,
                          bgcolor: p.factura_estado === 'pagada' ? '#d1fae5' : p.factura_estado === 'parcial' ? '#fef3c7' : '#dbeafe',
                          color: p.factura_estado === 'pagada' ? '#059669' : p.factura_estado === 'parcial' ? '#d97706' : '#2563eb',
                        }} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
